import { Fragment, useEffect, useState } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { useTranslation } from "react-i18next";
import { Form, Formik } from "formik";
import toast from "react-hot-toast";
import { mapErrors } from "../../../Utils";
import { ResourceLoading } from "../../../utils/ResourceLoading";
import { useDispatch } from "react-redux";
import PMListboxFormik from "../../../utils/PMListboxFormik";
import {
  getPdfReport,
  sendReport,
  checkSendReportStatus,
} from "../../../../api/service/report";
import { PMField } from "../../../utils/PMField";
import { useSelector } from "react-redux";
import { QuillEditor } from "../../../utils/quill-editor";
import PMSwitch from "../../../utils/PMSwitch";
import { fetchContact } from "../../../../api/customer/contact";

export const ReportSendDialog = (props) => {
  const { onLoading, onClose, open, isLoading, report } = props;
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const [documentUrl, setDocumentUrl] = useState(null);
  const [contact, setContact] = useState(null);
  const [isLoadingContact, setIsLoadingContact] = useState(false);

  const initialValues = {
    id: report?.id,
    sendReport: true,
    printReportType: "",
    email: "",
    name: "",
    allow_review: false,
    submit: null,
  };

  const currentUser = useSelector((state) => state.session.currentUser);

  const handleSend = async (payload) => {
    const response = await sendReport(payload);

    if (response?.data?.job_id) {
      // setJobId(response.data.job_id);
      // setJobStatus("queued");

      // // Start polling for job status
      // pollJobStatus(response.data.job_id);

      // Close dialog
      onClose();
    }

    return response;
  };

  const pollJobStatus = async (jobId) => {
    const maxAttempts = 60; // 5 minutes with 5-second intervals
    let attempts = 0;

    const poll = async () => {
      if (attempts >= maxAttempts) {
        setJobStatus("timeout");
        toast.error(
          t("Request timed out. Please check the report status manually.")
        );
        return;
      }

      try {
        const statusResponse = await checkSendReportStatus(jobId);
        const status = statusResponse?.data?.job_status;

        setJobStatus(status);

        if (status === "completed") {
          // Job completed successfully
          props.onRefresh(statusResponse?.data);
          props.onClose();
          toast.success(t("Report sent successfully!"));
          return;
        } else if (status === "failed") {
          // Job failed
          const errorMessage =
            statusResponse?.data?.metadata?.error ||
            t("Failed to send report. Please try again.");
          toast.error(errorMessage);
          return;
        } else if (status === "processing" || status === "queued") {
          // Still processing, continue polling
          attempts++;
          setTimeout(poll, 5000); // Poll every 5 seconds
        }
      } catch (error) {
        console.error("Error checking job status:", error);
        attempts++;
        setTimeout(poll, 5000);
      }
    };

    poll();
  };

  const handleRecciverChange = (recciver, formik) => {
    formik.setFieldValue("email", recciver?.email);
    formik.setFieldValue("name", recciver?.name);
    if (recciver?.email === report?.customer?.email) {
      formik.setFieldValue("printReportType", "customer_report");
    }
  };

  const [reportType, setReportType] = useState(null);
  const [numPages, setNumPages] = useState(null);
  const [pdfData, setPdfData] = useState(null);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [jobStatus, setJobStatus] = useState(null);
  const [jobId, setJobId] = useState(null);

  const fetchPdfReport = async () => {
    if (!report?.id) return false;

    const payload = {
      id: report?.id,
      params: {
        printReportType: reportType,
      },
    };
    await getPdfReport(payload)
      .then((response) => {
        setPdfUrl(response?.data?.pdf_url);
        setPdfData(response?.data);
      })
      .catch((err) => {
        console.log(err);
      });
  };

  const handleReportTypeChange = (data) => {
    setReportType(data?.value);
    if (reportType) {
      fetchPdfReport();
    }
  };

  useEffect(() => {
    const loadContact = async () => {
      if (open && report?.contact?.id) {
        setIsLoadingContact(true);
        try {
          const response = await fetchContact(report.contact.id);
          if (response?.data) {
            setContact(response.data);
          }
        } catch (error) {
          console.error("Error fetching contact:", error);
          toast.error(t("Failed to load contact information"));
        } finally {
          setIsLoadingContact(false);
        }
      } else {
        setContact(null);
      }
    };

    loadContact();
  }, [open, report?.contact?.id, t]);

  return (
    <>
      <Transition.Root show={open} as={Fragment}>
        <Dialog as="div" className="relative z-10" onClose={onClose}>
          <Transition.Child
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </Transition.Child>

          <div className="fixed inset-0 z-10 overflow-y-auto">
            <div className="flex min-h-full justify-center p-4 text-center sm:items-center sm:p-0">
              <Transition.Child
                enter="ease-out duration-300"
                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                enterTo="opacity-100 translate-y-0 sm:scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              >
                <Dialog.Panel className="w-full sm:max-w-2xl max-w-md transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                  <div className="mt-3 sm:mt-5">
                    <Dialog.Title
                      as="h3"
                      className="text-lg font-medium leading-6 text-gray-900"
                    >
                      {t("Send Report")}
                    </Dialog.Title>
                    <p className="text-red-500">
                      {t("Please note that this action cannot be undone")}
                    </p>

                    {isLoading ? (
                      <ResourceLoading />
                    ) : !report?.id ? (
                      "Missing ID"
                    ) : (
                      <Formik
                        initialValues={initialValues}
                        onSubmit={async (values, helpers) => {
                          try {
                            helpers.setSubmitting(true);

                            const response = await handleSend(values);

                            if (response?.data?.status === 200) {
                              // Job was queued successfully
                              toast.success(
                                t(
                                  "Report sending has been queued. You will be notified when it's complete."
                                )
                              );
                              helpers.setStatus({ success: true });
                            } else {
                              throw new Error(
                                mapErrors(response?.data?.errors) ||
                                  "Failed to queue report sending"
                              );
                            }
                          } catch (err) {
                            toast.error(
                              t(
                                "Something went wrong. Could not queue report sending."
                              )
                            );
                            console.error(err);
                            helpers.setStatus({ success: false });
                            helpers.setErrors({ submit: err.message });
                          } finally {
                            helpers.setSubmitting(false);
                          }
                        }}
                      >
                        {(formik) => (
                          <Form>
                            <div className="grid grid-cols-6 gap-6 mt-10">
                              <div className="col-span-6 sm:col-span-6">
                                <div className="flex flex-col">
                                  <span className="text-gray-400 text-sm mb-2">
                                    {t("Send to")}{" "}
                                  </span>
                                  <span className="isolate inline-flex rounded-md shadow-sm">
                                    <button
                                      onClick={() =>
                                        handleRecciverChange(
                                          {
                                            email: currentUser?.email,
                                            name: currentUser?.name,
                                          },
                                          formik
                                        )
                                      }
                                      type="button"
                                      className="relative inline-flex items-center rounded-l-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-10"
                                    >
                                      {t("Me")}
                                    </button>
                                    <button
                                      onClick={
                                        report?.customer?.email
                                          ? () =>
                                              handleRecciverChange(
                                                {
                                                  email:
                                                    report?.customer?.email,
                                                  name: report?.customer?.name,
                                                },
                                                formik
                                              )
                                          : () =>
                                              toast.error(
                                                "Customer Email is missing"
                                              )
                                      }
                                      type="button"
                                      className="relative -ml-px inline-flex items-center bg-white px-3 py-2 text-sm font-semibold text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-10"
                                    >
                                      {t("Customer")}
                                    </button>
                                    <button
                                      onClick={
                                        contact?.email
                                          ? () =>
                                              handleRecciverChange(
                                                {
                                                  email: contact?.email,
                                                  name: contact?.name || contact?.displayTitle,
                                                },
                                                formik
                                              )
                                          : () =>
                                              toast.error(
                                                t("Contact Email is missing")
                                              )
                                      }
                                      type="button"
                                      disabled={isLoadingContact || !contact?.email}
                                      className="relative -ml-px inline-flex items-center rounded-r-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-10 disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                      {isLoadingContact ? t("Loading...") : t("Contact")}
                                    </button>
                                  </span>
                                </div>
                              </div>
                              <div className="col-span-6 sm:col-span-6">
                                <PMField
                                  name="name"
                                  label={t("Name")}
                                  {...formik}
                                />
                              </div>
                              <div className="col-span-6 sm:col-span-6">
                                <PMField
                                  name="email"
                                  label={t("Email")}
                                  {...formik}
                                />
                              </div>
                              <div className="col-span-6 sm:col-span-6">
                                <PMListboxFormik
                                  label={t("Report Type")}
                                  name="printReportType"
                                  onChange={handleReportTypeChange}
                                  options={[
                                    {
                                      name: "printReportType",
                                      value: "report",
                                      label: t("Report"),
                                    },
                                    {
                                      name: "printReportType",
                                      value: "customer_report",
                                      label: t("Customer Report"),
                                    },
                                    {
                                      name: "printReportType",
                                      value: "note",
                                      label: t("Note"),
                                    },
                                  ]}
                                  {...formik}
                                />
                              </div>
                              <div className="col-span-6 sm:col-span-6">
                                <PMSwitch
                                  name="allow_review"
                                  label="Tillat Anmeldelse"
                                  {...formik}
                                />
                              </div>
                              <div className="col-span-6 sm:col-span-6 mb-[200px]">
                                <span className="text-sm px-1">
                                  Melding til kunde
                                </span>
                                <div className="mt-1">
                                  <QuillEditor
                                    name="message"
                                    rows={4}
                                    {...formik}
                                  />
                                </div>
                              </div>

                              {/* Job Status Indicator */}
                              {jobStatus && (
                                <div className="col-span-6 sm:col-span-6">
                                  <div className="bg-gray-50 p-4 rounded-lg">
                                    <div className="flex items-center">
                                      <div className="flex-shrink-0">
                                        {jobStatus === "queued" && (
                                          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
                                        )}
                                        {jobStatus === "processing" && (
                                          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-yellow-600"></div>
                                        )}
                                        {jobStatus === "completed" && (
                                          <div className="rounded-full h-5 w-5 bg-green-600 flex items-center justify-center">
                                            <svg
                                              className="h-3 w-3 text-white"
                                              fill="currentColor"
                                              viewBox="0 0 20 20"
                                            >
                                              <path
                                                fillRule="evenodd"
                                                d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                                clipRule="evenodd"
                                              />
                                            </svg>
                                          </div>
                                        )}
                                        {jobStatus === "failed" && (
                                          <div className="rounded-full h-5 w-5 bg-red-600 flex items-center justify-center">
                                            <svg
                                              className="h-3 w-3 text-white"
                                              fill="currentColor"
                                              viewBox="0 0 20 20"
                                            >
                                              <path
                                                fillRule="evenodd"
                                                d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                                                clipRule="evenodd"
                                              />
                                            </svg>
                                          </div>
                                        )}
                                      </div>
                                      <div className="ml-3">
                                        <p className="text-sm font-medium text-gray-900">
                                          {jobStatus === "queued" &&
                                            t("Report sending queued...")}
                                          {jobStatus === "processing" &&
                                            t(
                                              "Generating PDF and sending report..."
                                            )}
                                          {jobStatus === "completed" &&
                                            t("Report sent successfully!")}
                                          {jobStatus === "failed" &&
                                            t("Failed to send report")}
                                          {jobStatus === "timeout" &&
                                            t("Request timed out")}
                                        </p>
                                        {jobStatus === "queued" ||
                                        jobStatus === "processing" ? (
                                          <p className="text-sm text-gray-500">
                                            {t(
                                              "This may take a few minutes. You can close this dialog."
                                            )}
                                          </p>
                                        ) : null}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              )}
                            </div>
                            <div className="mt-20 sm:mt-6 sm:grid sm:grid-flow-row-dense sm:grid-cols-2 sm:gap-3">
                              <button
                                type="button"
                                className="inline-flex justify-center w-20 rounded-md border border-transparent bg-gray-600 px-4 py-2 text-base font-medium text-white shadow-sm hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 sm:text-sm"
                                onClick={onClose}
                                disabled={
                                  jobStatus === "queued" ||
                                  jobStatus === "processing"
                                }
                              >
                                {t("Close")}{" "}
                              </button>

                              <button
                                type="button"
                                className="inline-flex align-right justify-center rounded-md border border-transparent bg-emerald-600 px-4 py-2 text-base font-medium text-white shadow-sm hover:bg-blue-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                                onClick={formik.submitForm}
                                disabled={
                                  jobStatus === "queued" ||
                                  jobStatus === "processing"
                                }
                              >
                                {jobStatus === "queued" ||
                                jobStatus === "processing"
                                  ? t("Sending...")
                                  : t("Send")}
                              </button>
                            </div>
                          </Form>
                        )}
                      </Formik>
                    )}
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition.Root>
    </>
  );
};
